<template>
  <div>
    <v-navigation-drawer mobile-breakpoint="660" app clipped width="400" class="pt-8">
      <v-card class="px-4" flat>
        <v-card flat class="mb-4">
          <p class="mb-1 text-subtitle-2">Case</p>
          <InputSelectCase v-model="caseId" browse-btn-small disable-url-input></InputSelectCase>

          <template v-if="caseId">
            <template v-if="loadingOutputs.progress > 0">
              <v-card flat>
                <v-card-text class="py-0">
                  {{ loadingOutputs.text }}
                  <v-progress-linear
                    :value="loadingOutputs.progress"
                    color="primary"
                  ></v-progress-linear>
                </v-card-text>
              </v-card>
            </template>
          </template>
        </v-card>
      </v-card>
      <template v-if="caseObj && sqlSSEOutput.length">
        <v-card flat class="pa-4">
          <p class="mb-1 text-subtitle-2">Settings</p>
          <v-switch
            v-for="section in sections"
            :key="section.key"
            v-model="section.enabled"
            dense
            hide-details
            :label="section.title"
            :disabled="!section.available"
            :append-icon="section.editable ? 'mdi-pencil' : ''"
            @click:append="openSettings(section.key)"
          ></v-switch>
        </v-card>
      </template>

      <v-card class="pa-4" flat>
        <v-btn
          outlined
          color="primary"
          block
          rounded
          @click="downloadReport"
          :loading="pdfDownloadProgress.value > 0"
        >
          <v-icon class="mr-2">mdi-download</v-icon> download report
          <template #loader>
            <div class="custom-loader text-capitalize">
              {{ pdfDownloadProgress.text }}
              <v-progress-linear :value="pdfDownloadProgress.value"></v-progress-linear>
            </div>
          </template>
        </v-btn>
      </v-card>
    </v-navigation-drawer>
    <v-container>
      <v-card tile>
        <v-responsive :aspect-ratio="1 / Math.sqrt(2)">
          <v-card tile class="d-flex justify-center" flat>
            <vue-html2pdf
              :show-layout="true"
              :float-layout="false"
              :enable-download="true"
              :preview-modal="false"
              :paginate-elements-by-height="1400"
              :filename="currentTemplate"
              :pdf-quality="2"
              :manual-pagination="false"
              pdf-format="a4"
              pdf-content-width="800px"
              :html-to-pdf-options="pdfOptions"
              ref="html2Pdf"
              @progress="onProgress($event)"
              @beforeDownload="beforeDownload($event)"
              @hasStartedGeneration="hasStartedGeneration()"
              @hasGenerated="hasGenerated($event)"
            >
              <template #pdf-content>
                <!-- PDF Content Here -->
                <div class="v-application">
                  <div class="pdf-content">
                    <v-card flat>
                      <v-card flat class="d-flex justify-space-between align-end">
                        <img height="48" src="@/assets/rex-logo.png" />
                        <p class="text-subtitle-1 mb-0">{{ currentDate }}</p>
                      </v-card>
                      <v-card tile outlined>
                        <p class="mb-0 pl-1">
                          <strong>Case:</strong> {{ caseName }} / <strong>Solve date:</strong>
                          {{ solveDate }} / <strong>Market:</strong> {{ market }} /
                          <strong>Project:</strong> {{ projectName }}
                        </p>
                      </v-card>
                    </v-card>
                    <section class="pdf-item" v-if="showItem('description')">
                      <div>
                        <h2 class="section-title">Project Description</h2>
                        <v-row no-gutters>
                          <v-col sm="8">
                            <p class="section-paragraph">
                              {{ description }}
                            </p>
                          </v-col>
                          <v-col sm="4">
                            <v-card flat outlined class="mb-2 pt-2" height="200">
                              <ProjectDiagram :network="diagramNetwork" />
                            </v-card>
                          </v-col>
                        </v-row>
                      </div>
                    </section>
                    <section class="pdf-item" v-if="showItem('projectTable')">
                      <div>
                        <h2 class="section-title">Project Table</h2>
                        <v-row no-gutters class="justify-center">
                          <v-col sm="5">
                            <TableSimple :data="projectTable.tables"></TableSimple>
                          </v-col>
                          <v-col sm="7" v-if="projectTable.charts.length">
                            <Plotly
                              v-for="(chart, i) in projectTable.charts"
                              :key="`chart-projectTable-${i}`"
                              :layout="chart.layout"
                              :data="chart.data"
                              :display-mode-bar="false"
                            ></Plotly>
                          </v-col>
                        </v-row>
                      </div>
                    </section>
                    <section
                      class="pdf-item"
                      v-if="showItem('financialInputAssumptionsStorageServices')"
                    >
                      <div>
                        <h2 class="section-title">
                          {{ getTitle("financialInputAssumptionsStorageServices") }}
                        </h2>
                        <v-row class="justify-center">
                          <v-col
                            sm="6"
                            v-if="financialInputAssumptionsStorageServices.financialTable"
                          >
                            <TableSimple
                              :data="financialInputAssumptionsTable.financialTable"
                            ></TableSimple>
                          </v-col>
                          <v-col sm="6">
                            <TableSimple
                              :data="financialInputAssumptionsStorageServices.energyStorageServices"
                            ></TableSimple>
                          </v-col>
                        </v-row>
                      </div>
                    </section>
                    <section class="pdf-item" v-if="showItem('systemDispatch')">
                      <div>
                        <h2 class="section-title">System Dispatch</h2>
                        <Plotly
                          v-for="(chart, i) in systemDispatch.charts"
                          :key="`chart-projectTable-${i}`"
                          :layout="chart.layout"
                          :data="chart.data"
                          :display-mode-bar="false"
                        ></Plotly>
                        <v-row class="justify-space-around">
                          <v-col cols="6">
                            <TableSimple :data="systemDispatch.tables"></TableSimple>
                          </v-col>
                        </v-row>
                      </div>
                    </section>
                    <section class="pdf-item" v-if="showItem('energyPrices')">
                      <div>
                        <h2 class="section-title">Energy Prices</h2>
                        <SectionEnergyPrices
                          :case-obj="caseObj"
                          :settings="getSectionConfig('energyPrices').settings"
                        ></SectionEnergyPrices>
                      </div>
                    </section>
                    <section class="pdf-item" v-if="showItem('annualOutputs')">
                      <div>
                        <h2 class="section-title">Annual Outputs</h2>
                        <TableSimple :data="annualOutputs.tables"></TableSimple>
                      </div>
                    </section>
                  </div>
                </div>
              </template>
            </vue-html2pdf>
          </v-card>
        </v-responsive>
      </v-card>
    </v-container>
    <v-dialog v-model="sectionSettingsDialog.dialog" width="600">
      <v-card>
        <component
          :is="sectionSettingsDialog.component"
          v-model="sectionSettingsDialog.settings"
          :key="sectionSettingsDialog.key"
        ></component>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="saveSettingsDialog(sectionSettingsDialog.key)">
            save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import SettingsDescription from "@/components/reports/SettingsDescription.vue";
import SettingsEnergyPrices from "@/components/reports/SettingsEnergyPrices.vue";
import SettingsSystemDispatch from "@/components/reports/SettingsSystemDispatch.vue";
import { chartData, powerNominationData, reservePowerNominationData } from "@/mixins/outputs-utils";
import { mapReserveColor, mapServiceColor, mapServiceKey } from "@/mixins/services-utils";
import {
  parseSQLTimeSeriesResources,
  parseSQLTimeSeriesServices,
  parseSQLYearly,
} from "@/mixins/sql-utils";
import { DateTime } from "luxon";
import initSqlJs from "sql.js";
import VueHtml2pdf from "vue-html2pdf";
import { mapActions } from "vuex";

// eslint-disable-next-line import/no-webpack-loader-syntax
import sqlWasm from "!!file-loader?name=sql-wasm-[contenthash].wasm!sql.js/dist/sql-wasm.wasm";

const defaultSections = [
  {
    title: "Project Description",
    enabled: true,
    text: "",
    key: "description",
    settings: {
      content: "Project description...",
    },
    available: true,
    editable: true,
  },
  { title: "Project Table", enabled: true, key: "projectTable", available: true },
  {
    title: "Financing Assumptions and Energy Storage Services",
    enabled: true,
    key: "financialInputAssumptionsStorageServices",
    available: true,
  },
  { title: "Tariff", enabled: false, key: "tariff", available: false },
  {
    title: "System Dispatch",
    enabled: true,
    key: "systemDispatch",
    settings: {
      summerWeek: "2020-01-05",
      winterWeek: "2020-07-01",
    },
    available: true,
    editable: true,
  },
  { title: "Annual Outputs", enabled: true, key: "annualOutputs", available: true },
];

export default {
  name: "ReportView",
  components: {
    SectionEnergyPrices: () => import("@/components/reports/SectionEnergyPrices.vue"),
    InputSelectCase: () => import("@/components/InputSelectCase.vue"),
    Plotly: () => import("@/components/charts/Plotly.vue"),
    ProjectDiagram: () => import("@/components/ProjectDiagram.vue"),
    SettingsDescription,
    SettingsEnergyPrices,
    SettingsSystemDispatch,
    TableSimple: () => import("@/components/visualizations/TableSimple.vue"),
    VueHtml2pdf,
  },
  mixins: [],
  props: {},
  data() {
    return {
      currentReport: {
        inputs: {
          case: { caseId: null, projects: [] },
        },
      },
      sections: defaultSections,
      sectionSettingsDialog: { dialog: false, component: null, value: {} },
      loadingOutputs: {
        text: "Loading...",
        progress: 0,
      },
      dbSql: null,
      sqlSSEOutput: [],
      caseObj: null,
      job: null,
      profiles: [],
      currentDate: DateTime.now().toLocaleString(),
      templates: [{ text: "BTM BESS + Solar", value: "BTM_BESS_+_Solar" }],
      currentTemplate: "BTM_BESS_+_Solar",
      pdfDownloadProgress: {
        text: "Preparing file...",
        value: 0,
      },

      availableServices: ["EA", "PS", "BS", "MEA", "ESOCM", "RS"],
    };
  },
  computed: {
    caseId: {
      get() {
        return this.currentReport.inputs.case.caseId;
      },
      set(v) {
        this.currentReport.inputs.case.caseId = v;
        if (v) {
          this.loadCaseOutputs();
        }
      },
    },
    pdfOptions() {
      return {
        margin: 0.1,
        filename: `${this.caseName}.pdf`,
        image: {
          type: "jpeg",
          quality: 0.98,
        },
        enableLinks: true,
        html2canvas: {
          scale: 2,
        },
        jsPDF: {
          unit: "in",
          format: "a4",
        },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };
    },
    description() {
      return this.sections.find((v) => v.key === "description").settings.content;
    },
    caseName() {
      return this.caseObj ? this.caseObj.name : "";
    },
    solveDate() {
      if (!this.job) return "";
      return DateTime.fromISO(this.job.updated).toLocaleString(DateTime.DATETIME_SHORT);
    },
    market() {
      if (
        this.caseObj &&
        this.caseObj.input.projects.length &&
        this.caseObj.input.projects[0].markets.length
      ) {
        const [market] = this.caseObj.input.projects[0].markets;
        const { energy } = market;
        if (energy.source === "Market") {
          return energy.market_name;
        }
        return energy.source;
      }
      return "";
    },
    projectName() {
      return this.project ? this.project.name : "";
    },
    project() {
      if (this.caseObj && this.caseObj.input.projects.length) {
        return this.caseObj.input.projects[0];
      }
      return null;
    },
    projectTable() {
      // Storage
      let storageTable = {};
      let demandTable = {};
      let solarTable = {};
      const thermalTables = [];
      const charts = [];
      if (this.project) {
        // Storage
        if (this.project.storages.length) {
          const [storage] = this.project.storages;
          storageTable = {
            header: { text: `Storage ${storage.name} Ratings`, value: "" },
            rows: [
              ["Power Rating", `${storage.power_capacity[0]} MW`],
              ["Min and Max SOC level", `${storage.soc_min}% - ${storage.soc_max}%`],
              ["Energy Capacity", `${storage.energy_capacity[0]} MWh`],
              [
                "Round Trip Efficiency",
                `${this.$utils.formatNumberD3(
                  (storage.charging_efficiency * storage.charging_efficiency) / 100
                )}%`,
              ],
            ],
          };
        }
        // Solar
        if (this.project.generators.length) {
          const solar = this.project.generators.find((g) => g.source === "SOLAR_PV");
          if (solar) {
            solarTable = {
              header: { text: "Solar System Ratings", value: "" },
              rows: [["Installed Power Rating", `${solar.capacity[0]} MWac`]],
            };

            charts.push({
              data: this.profiles.find((v) => v.id === solar.generationProfile[0].id)
                ? [
                    {
                      y: this.profiles
                        .find((v) => v.id === solar.generationProfile[0].id)
                        .value.map((v) => v.value),
                      x: this.profiles
                        .find((v) => v.id === solar.generationProfile[0].id)
                        .value.map((v) => v.date),
                      marker: { color: this.$vuetify.theme.themes.light.renewablePV },
                    },
                  ]
                : [],
              layout: {
                title: { text: "Hourly Solar Generation", font: { size: 10 } },
                height: 320,
                yaxis: {
                  title: { text: "MW", font: { size: 10 } },
                  rangemode: "tozero",
                },
                margin: { t: 40, l: 60, r: 16 },
              },
            });
          }
          this.project.generators
            .filter((g) => g.type === "THERMAL" && g.active)
            .forEach((gen) => {
              thermalTables.push({
                header: { text: `Generator ${gen.name} Ratings`, value: "" },
                rows: [["Installed Power Rating", `${gen.capacity[0]} MW`]],
              });
            });
        }
        // Demand
        if (this.project.demands.length) {
          const [demand] = this.project.demands;
          const peakDemand = this.profiles.find((v) => v.id === demand.profile[0].id)
            ? Math.max(
                ...this.profiles
                  .find((v) => v.id === demand.profile[0].id)
                  .value.map((v) => v.value)
              )
            : "-";
          const annualDemand = this.profiles.find((v) => v.id === demand.profile[0].id)
            ? this.profiles
                .find((v) => v.id === demand.profile[0].id)
                .value.map((v) => v.value)
                .reduce((acc, v) => acc + v, 0)
            : "-";
          demandTable = {
            header: { text: "Energy Storage Ratings", value: "" },
            rows: [
              ["Peak Demand", `${this.$utils.formatNumberD3(peakDemand)} MW`],
              ["Annual Energy Demand", `${this.$utils.formatNumberD3(annualDemand)} MWh`],
            ],
          };
          charts.push({
            data: this.profiles.find((v) => v.id === demand.profile[0].id)
              ? [
                  {
                    y: this.profiles
                      .find((v) => v.id === demand.profile[0].id)
                      .value.map((v) => v.value),
                    x: this.profiles
                      .find((v) => v.id === demand.profile[0].id)
                      .value.map((v) => v.date),
                    fill: "tozeroy",
                    marker: { color: this.$vuetify.theme.themes.light.demand },
                  },
                ]
              : [],
            layout: {
              title: { text: "Hourly Demand", font: { size: 10 } },
              height: 320,
              yaxis: {
                title: { text: "MW", font: { size: 10 } },
                rangemode: "tozero",
              },
              margin: { t: 40, l: 60, r: 16 },
            },
          });
        }
      }

      return {
        tables: !this.caseObj
          ? [
              {
                header: { text: "Energy Storage Ratings", value: "" },
                rows: [["Storage ratings..."]],
              },
              {
                header: { text: "Solar System Ratings", value: "" },
                rows: [["Solar system ratings..."]],
              },
              {
                header: { text: "Demand Ratings", value: "" },
                rows: [["Demand ratings..."]],
              },
            ]
          : [storageTable, solarTable, ...thermalTables, demandTable],
        charts: !this.caseObj
          ? [
              {
                data: [],
                layout: {
                  title: { text: "Hourly Solar Generation", font: { size: 10 } },
                  height: 320,
                  yaxis: {
                    title: { text: "MW", font: { size: 10 } },
                  },
                  margin: { t: 40, l: 60, r: 16 },
                },
              },
            ]
          : charts,
      };
    },
    financialInputAssumptionsStorageServices() {
      let storageServices = [["Energy storage services..."]];
      let storageYearlyResult;
      if (this.sqlSSEOutput.length) {
        const projectOutput = this.sqlSSEOutput[0][Object.keys(this.sqlSSEOutput[0])[0]];
        storageYearlyResult =
          projectOutput.yearlyResults[Object.keys(projectOutput.yearlyResults)[0]].storages[
            Object.keys(
              projectOutput.yearlyResults[Object.keys(projectOutput.yearlyResults)[0]].storages
            )[0]
          ];
      }
      if (storageYearlyResult) {
        storageServices = Object.keys(storageYearlyResult)
          .filter((key) => this.availableServices.includes(key))
          .map((key) => [mapServiceKey(key)]);
      }
      return {
        energyStorageServices: [
          {
            header: { text: "Energy Storage Services" },
            rows: storageServices,
          },
        ],
      };
    },
    systemDispatch() {
      if (!this.sections.find((v) => v.key === "systemDispatch")) {
        return { charts: [], tables: [] };
      }
      let dataArray = [];
      let storageYearlyResult = {};
      if (this.sqlSSEOutput.length) {
        const projectOutput = this.sqlSSEOutput[0][Object.keys(this.sqlSSEOutput[0])[0]];
        storageYearlyResult =
          projectOutput.yearlyResults[Object.keys(projectOutput.yearlyResults)[0]].storages[
            Object.keys(
              projectOutput.yearlyResults[Object.keys(projectOutput.yearlyResults)[0]].storages
            )[0]
          ];
        const storageOutput = projectOutput.storages[Object.keys(projectOutput.storages)[0]];
        dataArray = storageOutput[Object.keys(storageOutput)[0]];
      }

      const summerData = this.sqlSSEOutput.length
        ? [
            {
              name: "Total Power Nomination",
              ...chartData(dataArray, "totPowNom", this.sections[4].settings.summerWeek),
              line: {
                shape: "hv",
                color: "#000000",
              },
            },
            ...Object.entries(dataArray[0])
              .filter(([serviceKey]) => this.availableServices.includes(serviceKey))
              .map(([serviceKey, serviceObj]) => ({
                name: serviceObj.name,
                ...powerNominationData(dataArray, serviceKey, this.sections[4].settings.summerWeek),
                type: "bar",
                marker: {
                  color: mapServiceColor(serviceKey),
                },
                offset: 0.5,
                width: this.caseObj.settings.timeline_settings.time_interval * 60 * 1000,
              })),
            ...(dataArray[0].R
              ? Object.keys(dataArray[0].R).map((reserveKey, reserveIndex) => ({
                  name: `Res. ${reserveKey}`,
                  ...reservePowerNominationData(
                    dataArray,
                    reserveKey,
                    this.sections[4].settings.summerWeek
                  ),
                  type: "bar",
                  marker: {
                    color: mapReserveColor(reserveIndex),
                  },
                  offset: 0.5,
                  width: this.caseObj.settings.timeline_settings.time_interval * 60 * 1000,
                }))
              : []),
          ]
        : [];
      const winterData = this.sqlSSEOutput.length
        ? [
            {
              name: "Total Power Nomination",
              ...chartData(dataArray, "totPowNom", this.sections[4].settings.winterWeek),
              line: {
                shape: "hv",
                color: "#000000",
              },
            },
            ...Object.entries(dataArray[0])
              .filter(([serviceKey]) => this.availableServices.includes(serviceKey))
              .map(([serviceKey, serviceObj]) => ({
                name: serviceObj.name,
                ...powerNominationData(dataArray, serviceKey, this.sections[4].settings.winterWeek),
                type: "bar",
                marker: {
                  color: mapServiceColor(serviceKey),
                },
                offset: 0.5,
                width: this.caseObj.settings.timeline_settings.time_interval * 60 * 1000,
              })),
            ...(dataArray[0].R
              ? Object.keys(dataArray[0].R).map((reserveKey, reserveIndex) => ({
                  name: `Res. ${reserveKey}`,
                  ...reservePowerNominationData(
                    dataArray,
                    reserveKey,
                    this.sections[4].settings.winterWeek
                  ),
                  type: "bar",
                  marker: {
                    color: mapReserveColor(reserveIndex),
                  },
                  offset: 0.5,
                  width: this.caseObj.settings.timeline_settings.time_interval * 60 * 1000,
                }))
              : []),
          ]
        : [];
      const charts = [
        {
          data: summerData,
          layout: {
            title: { text: "Typical Summer Week Dispatch", font: { size: 14 } },
            height: 340,
            yaxis: {
              title: "Dispatch MW",
            },
            legend: { orientation: "h" },
          },
        },
        {
          data: winterData,
          layout: {
            title: { text: "Typical Winter Week Dispatch", font: { size: 14 } },
            height: 340,
            yaxis: {
              title: "Dispatch MW",
            },
            legend: { orientation: "h" },
          },
        },
      ];
      const tables = this.sqlSSEOutput.length
        ? [
            {
              header: ["Annual Hourly Dispatch Results", ""],
              rows: [
                ["Cycles", `${this.$utils.formatNumberD3(storageYearlyResult.cycles)} cycles/yr`],
                [
                  "Throughput",
                  `${this.$utils.formatNumberD3(storageYearlyResult.energyThroughput)} MWh/yr`,
                ],
              ],
            },
          ]
        : [];
      return { charts, tables };
    },
    annualOutputs() {
      const tables = [];
      if (this.sqlSSEOutput.length) {
        const projectOutput = this.sqlSSEOutput[0][Object.keys(this.sqlSSEOutput[0])[0]];
        const storageYearlyResult =
          projectOutput.yearlyResults[Object.keys(projectOutput.yearlyResults)[0]].storages[
            Object.keys(
              projectOutput.yearlyResults[Object.keys(projectOutput.yearlyResults)[0]].storages
            )[0]
          ];
        const marketYearlyOutput =
          projectOutput.yearlyResults[Object.keys(projectOutput.yearlyResults)[0]].markets[
            Object.keys(
              projectOutput.yearlyResults[Object.keys(projectOutput.yearlyResults)[0]].markets
            )[0]
          ];
        const generatorYearlyOutput =
          projectOutput.yearlyResults[Object.keys(projectOutput.yearlyResults)[0]].generators[
            Object.keys(
              projectOutput.yearlyResults[Object.keys(projectOutput.yearlyResults)[0]].generators
            )[0]
          ];
        if (storageYearlyResult) {
          const rows = [
            ["Consumption (MWh)", this.$utils.formatNumberD3(storageYearlyResult.discharge)],
            ["Generation (MWh)", this.$utils.formatNumberD3(storageYearlyResult.charge)],
            ["Cycle Count", this.$utils.formatNumberD3(storageYearlyResult.cycles)],
            [
              "Energy Throughput (MWh-yr)",
              this.$utils.formatNumberD3(storageYearlyResult.energyThroughput),
            ],
            ["Purchases ($-yr)", this.$utils.formatNumberD3(storageYearlyResult.purchases)],
            ["Sales ($-yr)", this.$utils.formatNumberD3(storageYearlyResult.sales)],
            ["P&L ($)", this.$utils.formatNumberD3(storageYearlyResult.pL)],
            ["Operation Hours (h)", this.$utils.formatNumberD3(storageYearlyResult.operationHours)],
          ];

          tables.push({
            header: { text: `Storage ${storageYearlyResult.name}: Annual Results`, value: "" },
            rows,
          });
          Object.entries(storageYearlyResult)
            .filter(([key]) => this.availableServices.includes(key))
            .forEach(([_key, output]) => {
              const serviceRow = [
                ["Consumption (MWh)", this.$utils.formatNumberD3(output.discharge)],
                ["Generation (MWh)", this.$utils.formatNumberD3(output.charge)],
                ["Power Allocation (MW-yr)", this.$utils.formatNumberD3(output.powerAllocation)],
                ["Energy Allocation (MWh-yr)", this.$utils.formatNumberD3(output.energyAllocation)],
                ["Purchases ($-yr)", this.$utils.formatNumberD3(output.purchases)],
                ["Sales ($-yr)", this.$utils.formatNumberD3(output.sales)],
                ["P&L ($)", this.$utils.formatNumberD3(output.pL)],
                ["Operation Hours (h)", this.$utils.formatNumberD3(output.operationHours)],
              ];
              tables.push({
                header: {
                  text: `Storage ${storageYearlyResult.name} [${mapServiceKey(
                    output.serviceName
                  )}]: Annual Results`,
                  value: "",
                },
                rows: serviceRow,
              });
            });
        }
        if (generatorYearlyOutput) {
          const rows = [
            ["Generation (MWh-yr)", this.$utils.formatNumberD3(generatorYearlyOutput.generation)],
            ["Curtailment (%-yr)", this.$utils.formatNumberD3(generatorYearlyOutput.curtailment)],
            [
              "Available Resources (MWh-yr)",
              this.$utils.formatNumberD3(generatorYearlyOutput.availableResource),
            ],
            ["Sales ($-yr)", this.$utils.formatNumberD3(generatorYearlyOutput.sales)],
            ["P&L ($-yr)", this.$utils.formatNumberD3(generatorYearlyOutput.pL)],
          ];
          tables.push({
            header: { text: `Generator ${generatorYearlyOutput.name}:  Annual Results`, value: "" },
            rows,
          });
        }
        if (marketYearlyOutput) {
          const rows = [
            ["Purchases ($-yr)", this.$utils.formatNumberD3(marketYearlyOutput.purchases)],
            ["Imports (MWh-yr)", this.$utils.formatNumberD3(marketYearlyOutput.imports)],
            ["Sales ($-yr)", this.$utils.formatNumberD3(marketYearlyOutput.sales)],
            ["Exports (MWh-yr)", this.$utils.formatNumberD3(marketYearlyOutput.exports)],
          ];
          tables.push({
            header: { text: `Market ${marketYearlyOutput.name}: Annual Results`, value: "" },
            rows,
          });
        }
      }
      return { tables };
    },
    computedSections() {
      return this.sections
        .filter((v) => v.enabled)
        .map((section) => ({ ...section, content: this[section.key] }));
    },
    diagramNetwork() {
      if (!this.project) return {};
      return {
        bus: [{ id: this.project.id, name: this.project.name }],
        storages: this.project.storages.map((s) => ({
          id: s.id,
          busId: this.project.id,
          name: s.name,
          breaker: s.active ? "close" : "open",
        })),
        generators: this.project.generators.map((g) => ({
          id: g.id,
          busId: this.project.id,
          name: g.name,
          breaker: g.active ? "close" : "open",
          type: g.type === "THERMAL" ? "THERMAL" : g.source,
        })),
        loads: this.project.demands.map((d) => ({
          id: d.id,
          busId: this.project.id,
          name: d.name,
          breaker: d.active ? "close" : "open",
        })),
        markets: this.project.markets.map((m) => ({
          id: m.id,
          busId: this.project.id,
          name: m.name,
          breaker: m.energy ? "close" : "open",
        })),
        switches: [],
      };
    },
  },
  watch: {},
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    ...mapActions({
      findProfiles: "profiles/find",
      findOutputs: "outputs/find",
      getJob: "jobs/get",
      getCase: "cases/get",
    }),
    downloadReport() {
      this.pdfDownloadProgress.value = 10;
      this.$refs.html2Pdf.generatePdf();
    },
    onProgress($event) {
      this.pdfDownloadProgress.value = $event;
      if ($event === 100) {
        this.pdfDownloadProgress.value = 0;
      }
    },
    loadCaseOutputs() {
      this.loadingOutputs = {
        text: "Loading case...",
        progress: 5,
      };
      this.getCase(this.caseId).then((caseObj) => {
        this.caseObj = caseObj;
        this.setSections();
        const timeInterval = caseObj.settings.timeline_settings.time_interval;
        this.loadingOutputs = {
          text: "Loading profiles...",
          progress: 25,
        };
        this.getProfiles().then(() => {
          this.loadingOutputs = {
            text: "Loading outputs...",
            progress: 75,
          };
          this.findOutputs({ query: { case_id: this.caseId, $select: ["id", "output_sql"] } }).then(
            (r) => {
              if (
                r.total &&
                r.data[0].type_name === "STACKED_SERVICES_EMULATOR" &&
                r.data[0].output_sql
              ) {
                this.loadingOutputs = {
                  text: "Loading outputs...",
                  progress: 95,
                };
                this.getJob(r.data[0].id).then((job) => {
                  this.job = job;
                });
                this.parseSSESqlOutputs(r.data[0].output_sql, timeInterval);
              } else {
                this.loadingOutputs = {
                  text: "Getting outputs...",
                  progress: 0,
                  error: "No outputs found for selected case",
                };
              }
            }
          );
        });
      });
    },
    parseSSESqlOutputs(sqlOutput, timeInterval) {
      initSqlJs({ locateFile: () => sqlWasm }).then((SQL) => {
        this.loadingOutputs = {
          text: "Processing  outputs file...",
          progress: 35,
        };
        this.dbSql = new SQL.Database(Buffer.from(sqlOutput.data));
        const [response] = this.dbSql.exec("SELECT * FROM PROJECTS");
        if (response) {
          const { columns } = response;
          const projects = response.values;
          projects.forEach((project) => {
            let projectObj = columns.reduce(
              (acc, key, index) => ({ ...acc, [key]: project[index] }),
              {}
            );
            // Yearly Results
            projectObj = parseSQLYearly(projectObj, this.dbSql);
            // Time series results
            projectObj = parseSQLTimeSeriesResources(projectObj, this.dbSql);
            // Time series services
            projectObj = parseSQLTimeSeriesServices(projectObj, this.dbSql, timeInterval);
            this.sqlSSEOutput.push({ [projectObj.name]: Object.freeze(projectObj) });
            this.loadingOutputs.progress += 1;
          });
        }
        this.dbSql.close();
        this.loadingOutputs = {
          text: "Loading...",
          progress: 0,
        };
      });
    },
    setSections() {
      const sectionsCopy = this.sections;
      // Set description
      sectionsCopy.find(
        (v) => v.key === "description"
      ).settings.content = `${this.caseObj.name}. ${this.caseObj.notes}`;

      if (!this.currentReport.inputs.dcfCase) {
        if (sectionsCopy.find((v) => v.key === "financialInputAssumptionsStorageServices")) {
          sectionsCopy.find((v) => v.key === "financialInputAssumptionsStorageServices").title =
            "Energy Storage Services";
        }
      }
      // Storage specific sections
      if (!this.caseObj.input.projects.length || !this.caseObj.input.projects[0].storages.length) {
        if (!this.caseObj.input.projects[0].storages.length) {
          sectionsCopy.splice(
            sectionsCopy.findIndex((v) => v.key === "financialInputAssumptionsStorageServices"),
            1
          );
          sectionsCopy.splice(
            sectionsCopy.findIndex((v) => v.key === "systemDispatch"),
            1
          );
        } else {
          if (!sectionsCopy.find((v) => v.key === "systemDispatch")) {
            sectionsCopy.push(defaultSections.find((v) => v.key === "systemDispatch"));
          }
          if (!sectionsCopy.find((v) => v.key === "financialInputAssumptionsStorageServices")) {
            sectionsCopy.push({
              ...defaultSections.find((v) => v.key === "financialInputAssumptionsStorageServices"),
              title: "Energy Storage Services",
            });
          }
        }
      }
      sectionsCopy.find((v) => v.key === "systemDispatch").settings = {
        summerWeek: DateTime.fromISO(this.caseObj.settings.timeline_settings.start_date)
          .plus({ days: 7 })
          .startOf("week")
          .toISODate(),
        winterWeek: DateTime.fromISO(this.caseObj.settings.timeline_settings.start_date)
          .plus({ days: 7, months: 6 })
          .startOf("week")
          .toISODate(),
      };
      // Energy Prices
      if (this.caseObj.input.projects.length && this.caseObj.input.projects[0].markets.length) {
        sectionsCopy.push({
          title: "Energy Prices",
          enabled: true,
          key: "energyPrices",
          editable: true,
          available: true,
          settings: {
            text: "Add text here...",
          },
        });
      }
      this.sections = sectionsCopy;
    },
    getProfiles() {
      const profilesIds = [];
      if (this.caseObj && this.caseObj.input.projects.length) {
        // Solar Profile
        if (this.caseObj.input.projects[0].generators.length) {
          const solar = this.caseObj.input.projects[0].generators.find(
            (g) => g.source === "SOLAR_PV"
          );
          if (solar) {
            profilesIds.push(solar.generationProfile[0].id);
          }
        }
        // Demand Profile
        if (this.caseObj.input.projects[0].demands.length) {
          const [demand] = this.caseObj.input.projects[0].demands;
          profilesIds.push(demand.profile[0].id);
        }
      }

      const params = {
        query: { id: { $in: profilesIds } },
      };

      return this.findProfiles(params).then((r) => {
        this.profiles = r;
      });
    },
    showItem(key) {
      return this.sections.find((section) => section.key === key)
        ? this.sections.find((section) => section.key === key).enabled
        : false;
    },
    getTitle(key) {
      return this.sections.find((section) => section.key === key)
        ? this.sections.find((section) => section.key === key).title
        : "";
    },
    openSettings(key) {
      const components = {
        systemDispatch: "SettingsSystemDispatch",
        description: "SettingsDescription",
        energyPrices: "SettingsEnergyPrices",
      };
      this.sectionSettingsDialog.dialog = true;
      this.sectionSettingsDialog.component = components[key];
      this.sectionSettingsDialog.key = key;
      this.sectionSettingsDialog.settings = this.sections.find((v) => v.key === key).settings;
    },
    saveSettingsDialog(key) {
      this.sections.find((v) => v.key === key).settings = this.sectionSettingsDialog.settings;
      this.sectionSettingsDialog.dialog = false;
    },
    getSectionConfig(key) {
      return this.sections.find((v) => v.key === key);
    },
  },
};
</script>

<style>
.theme--light.v-application {
  background-color: rgb(82, 86, 89);
}
.report {
  background-color: white;
}
.pdf-content {
  background-color: white;
  width: 780px;
  padding: 20px;
}
.section-title {
  font-size: 1.5em;
  font-weight: 600;
  margin: 24px 0 16px;
  padding-bottom: 0.3em;
  border-bottom: 1px solid #8b8989;
  color: #303030;
}
.table-header {
  background-color: #306d8a;
}
.theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  color: white;
}
.custom-loader {
  width: 90%;
}
</style>
